import { useState, Fragment } from 'react';
import { useMutation } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from 'tss-react/mui';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import type { Group } from '../../types/Group';
import { useAppContext } from '../../contexts/app';
import { updateGroupMemberPostNotificationPreferenceMutation } from '../../apollo/groups';

const useStyles = makeStyles()((theme) => ({
  button: {
    padding: theme.spacing(0, 1),
  },
  buttonIcon: {
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
  buttonText: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}));

type Props = {
  group?: Group;
};

const GroupPostNotificationPreferenceToggle = ({ group: { membership } }: Props) => {
  const { isLoggedIn } = useAppContext();
  const [updateGroupMemberPostNotificationPreference] = useMutation(updateGroupMemberPostNotificationPreferenceMutation);
  const [submitting, setSubmitting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { classes } = useStyles();

  if (!isLoggedIn || !membership) {
    return null;
  }

  function onClick(preference) {
    return () => {
      setAnchorEl(null);
      setSubmitting(true);
      updateGroupMemberPostNotificationPreference({
        variables: {
          id: membership.id,
          preference
        }
      }).then(() => setSubmitting(false));
    };
  }

  return (
    <Fragment>
      <Button
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
        disabled={submitting}
        aria-controls="group-post-notification-preference-menu"
        aria-haspopup="true"
        color="primary"
        // variant="outlined"
        className={classes.button}
      >
        {membership.postNotificationPreference === 'ALL' && (
          <Fragment>
            <NotificationsActiveIcon className={classes.buttonIcon} />
            <span className={classes.buttonText}>All New Posts and Comments</span>
          </Fragment>
        )}
        {membership.postNotificationPreference === 'FOLLOWING' && (
          <Fragment>
            <NotificationsActiveIcon className={classes.buttonIcon} />
            <span className={classes.buttonText}>New Posts</span>
          </Fragment>
        )}
        {membership.postNotificationPreference === 'NONE' && (
          <Fragment>
            <NotificationsNoneIcon className={classes.buttonIcon} />
            <span className={classes.buttonText}>Unsubscribed</span>
          </Fragment>
        )}
      </Button>
      <Menu
        id="group-post-notification-preference-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <MenuItem
          onClick={onClick('FOLLOWING')}
          disabled={membership.postNotificationPreference === 'FOLLOWING'}
        >
          Subscribe to posts
        </MenuItem>
        <MenuItem
          onClick={onClick('ALL')}
          disabled={membership.postNotificationPreference === 'ALL'}
        >
          Subscribe to posts and comments
        </MenuItem>
        <MenuItem
          onClick={onClick('NONE')}
          disabled={membership.postNotificationPreference === 'NONE'}
        >
          Unsubscribe from posts
        </MenuItem>
      </Menu>
    </Fragment>
  );
};

export default GroupPostNotificationPreferenceToggle;
